import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useGetGymsQuery, useDeleteGymMutation } from "../../services";
import { useAppSelector } from "../../redux/hooks";
import { selectOrganisationId } from "../../redux/organisationReducer";
import { RootState } from "../../redux/store";
import { GymRow } from "../../types/gym";
import { LicensesType } from "../../types/subscription/subscriptionScreen/subscriptionScreen.type";
import { adminRoutes } from "../../routers/AdminRouters";
import { formatPhoneDisplay } from "../../utils/formatPhoneDisplay";
import { QRCodeSVG } from "qrcode.react";
import { FaCheck, FaCopy } from "react-icons/fa";
import { toast } from "../../core/toast";
import html2canvas from "html2canvas";
const GymList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Get selected organization ID from Redux
  const organisationId = useAppSelector(selectOrganisationId);
  // Load from RTK with organization filter
  const {
    data: gymsData,
    isLoading,
    isFetching,
    error,
  } = useGetGymsQuery({ organisationId, license: 1 });
  const [deleteGym, { isLoading: isDeleting }] = useDeleteGymMutation();

  const [deleteTarget, setDeleteTarget] = useState<GymRow | null>(null);
  const openDelete = (row: GymRow) => setDeleteTarget(row);
  const [guestQrModalOpen, setGuestQrModalOpen] = useState<boolean>(false);
  const closeDelete = () => setDeleteTarget(null);
  const [qyGymId, setQrGymid] = useState<string>("");
  const captureRef = useRef<HTMLDivElement | null>(null);
  const [isDownloading, setIsdownloading] = useState<boolean>(false);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteGym(deleteTarget.id).unwrap();
    } catch (e) {
      console.error("Delete gym failed:", e);
    } finally {
      closeDelete();
    }
  };
  const HandleOpenGuestQrModal = async (
    id: string,
    allowGuestAccess: boolean,
  ) => {
    try {
      if (!allowGuestAccess) {
        toast.error("Guest access is not enabled for this gym");
        return;
      }

      setGuestQrModalOpen(true);
      setQrGymid(id);
    } catch (error) {}
  };

  const handleCopy = async () => {
    try {
      // ✅ Copy link
      await navigator.clipboard.writeText(
        `${import.meta.env.VITE_USER_APP_URL}/gym/${qyGymId}`,
      );
      toast.success("Link copied to clipboard 📋");
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDownload = async () => {
    try {
      if (!captureRef.current) return;
      setIsdownloading(true);
      const canvas = await html2canvas(captureRef.current, {
        scale: 2,
      });

      const link = document.createElement("a");
      link.download = "qr-code.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error(error);
    } finally {
      setIsdownloading(false);
    }
  };
  // Map API to UI rows (support nested screens/contact and raw fields)
  const rows: GymRow[] = (gymsData ?? []).map((g: any) => ({
    id: String(g.gymid ?? g._id ?? g.id ?? ""),
    allowGuestAccess: g.allowGuestAccess ?? false,
    gymName: g.name ?? "",
    organisation:
      typeof g.organisation === "object"
        ? g.organisation?.displayName ||
          g.organisation?.name ||
          g.organisation?.title ||
          ""
        : g.organisation || "",
    premium: Array.isArray(g.licenses)
      ? g.licenses.filter(
          (l: any) =>
            String(l.type).toUpperCase() === LicensesType.PREMIUM.toUpperCase(),
        ).length
      : 0,
    basic: Array.isArray(g.licenses)
      ? g.licenses.filter(
          (l: any) =>
            String(l.type).toUpperCase() === LicensesType.BASIC.toUpperCase(),
        ).length
      : 0,
    location: g.location ?? "",
    contactName: g.contact?.name ?? g.contactPerson ?? "",
    contactNumber: String(g.contact?.phone ?? g.phone ?? g.phoneNumber ?? ""),
  }));

  // THEME CLASSES
  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
  const heading = "text-text";
  const crumbLink = theme === "dark" ? "text-gray-500" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

  const cardClass =
    theme === "dark"
      ? "bg-[#1A1A1A] border-0 text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const cardHeader =
    theme === "dark"
      ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between"
      : "px-6 py-4 border-b border-gray-200 text-gray-800 flex items-center justify-between";

  const thClass =
    theme === "dark"
      ? "px-6 py-3 text-left text-sm font-boldgr text-[#F3F3F6]  tracking-wider border-b border-[#2A2A2A]"
      : "px-6 py-3 text-left text-sm font-bold text-gray-700  tracking-wider border-b border-gray-200";

  const tdClass =
    theme === "dark"
      ? "px-6 py-4 text-sm text-text-table border-b border-[#2A2A2A]"
      : "px-6 py-4 text-sm text-text-table border-b border-gray-200";

  const iconHover = theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5";
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-6">
        <h1 className={`text-lg font-medium ${heading} mb-2`}>Gyms Overview</h1>
        <div className="text-sm flex items-center gap-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Link
            to="/"
            className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
          >
            Dashboard
          </Link>
          <span className={crumbSep}>&gt;</span>
          <span className="text-[#B6CB24] font-normal">All Gyms</span>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden shadow-lg`}>
        <div className={cardHeader}>
          <span className="text-base font-medium">All Gym Details</span>

          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#B6CB24] text-[#B6CB24] hover:bg-[#B6CB24]/10 text-sm font-medium"
            onClick={() => navigate(adminRoutes.gymCreate)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2.00012C17.5228 2.00012 22 6.47727 22 12.0001C22 17.523 17.5228 22.0001 12 22.0001C6.47715 22.0001 2 17.523 2 12.0001M8.909 2.48699C7.9 2.8146 6.96135 3.29828 6.12153 3.90953M3.90943 6.12162C3.29806 6.9616 2.81432 7.90044 2.4867 8.90964"
                stroke="#F3F3F6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 8.00012V16.0001M16 12.0001H8"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Create new gym
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={thClass}>Gym Name</th>
                <th className={thClass}>Organisation</th>
                {/* <th className={thClass}> {LicensesType.PREMIUM}</th>
                <th className={thClass}> {LicensesType.BASIC}</th> */}
                <th className={thClass}>Premium Licenses </th>
                <th className={thClass}>Basic Licenses</th>
                <th className={thClass}>Contact Person</th>
                <th className={thClass}>Contact Number</th>
                <th className={thClass}>Action</th>
              </tr>
            </thead>

            <tbody>
              {isLoading || isFetching ? (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={8}>
                    Loading gyms...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={8}>
                    Failed to load gyms.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={8}>
                    No gyms found.Create a new gym to get started.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      theme === "dark"
                        ? "hover:bg-[#222222]"
                        : "hover:bg-gray-50"
                    }
                  >
                    <td className={tdClass}>{r.gymName}</td>
                    <td className={tdClass}>{r.organisation}</td>
                    <td className={tdClass}>{r.premium}</td>
                    <td className={tdClass}>{r.basic}</td>
                    <td className={tdClass}>{r.contactName}</td>
                    <td className={tdClass}>
                      {formatPhoneDisplay(r.contactNumber)}
                    </td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-2">
                        <button
                          title="Assign License"
                          className={`p-1.5 rounded transition-colors ${iconHover}`}
                          onClick={() =>
                            navigate(
                              adminRoutes.gymAssignLicenses.replace(
                                ":gymid",
                                r.id,
                              ),
                            )
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M21.8648 3H2.67613C2.49681 3 2.32483 3.07124 2.19804 3.19804C2.07124 3.32483 2 3.49681 2 3.67613V16.6444C2 16.8237 2.07124 16.9957 2.19804 17.1225C2.32483 17.2493 2.49681 17.3205 2.67613 17.3205H10.1745V19.9777H3.27789C3.09857 19.9777 2.92659 20.049 2.79979 20.1758C2.67299 20.3026 2.60176 20.4745 2.60176 20.6539C2.60176 20.8332 2.67299 21.0052 2.79979 21.132C2.92659 21.2588 3.09857 21.33 3.27789 21.33H21.2631C21.4424 21.33 21.6144 21.2588 21.7412 21.132C21.868 21.0052 21.9392 20.8332 21.9392 20.6539C21.9392 20.4745 21.868 20.3026 21.7412 20.1758C21.6144 20.049 21.4424 19.9777 21.2631 19.9777H14.3665V17.3205H21.8648C22.0441 17.3205 22.2161 17.2493 22.3429 17.1225C22.4697 16.9957 22.541 16.8237 22.541 16.6444V3.67613C22.541 3.49681 22.4697 3.32483 22.3429 3.19804C22.2161 3.07124 22.0441 3 21.8648 3ZM13.0142 19.971H11.5267V17.3205H13.0142V19.971ZM21.1887 15.9615H3.35227V4.35227H21.1887V15.9615Z"
                              fill="white"
                            />
                            <path
                              d="M11.5941 12.6288C11.6573 12.6915 11.7323 12.741 11.8146 12.7747C11.897 12.8083 11.9852 12.8254 12.0742 12.8249C12.1632 12.8254 12.2514 12.8083 12.3338 12.7747C12.4162 12.741 12.4911 12.6915 12.5543 12.6288L15.7186 9.45773C15.8293 9.32838 15.8872 9.162 15.8806 8.99183C15.8741 8.82167 15.8035 8.66025 15.6831 8.53983C15.5627 8.41942 15.4013 8.34887 15.2311 8.3423C15.0609 8.33573 14.8946 8.39361 14.7652 8.50438L12.0607 11.2089L10.7084 9.85665C10.5775 9.75989 10.4161 9.7136 10.2538 9.72629C10.0915 9.73897 9.93928 9.80977 9.825 9.92569C9.71073 10.0416 9.64211 10.1949 9.63174 10.3573C9.62138 10.5198 9.66997 10.6805 9.76858 10.81L11.5941 12.6288Z"
                              fill="#C6D92C"
                            />
                          </svg>
                        </button>

                        <button
                          title="Open guest qr"
                          className={`p-1.5 rounded transition-colors ${iconHover}`}
                          onClick={() => {
                            HandleOpenGuestQrModal(r.id, r.allowGuestAccess);
                          }}
                        >
                          <img
                            src="assets/dark-theme/dashboard/qrCode_gym.svg"
                            alt="no icon"
                          ></img>
                        </button>
                        <button
                          title="Edit"
                          className={`p-1.5 rounded transition-colors ${iconHover}`}
                          onClick={() =>
                            navigate(
                              adminRoutes.gymEdit.replace(":gymid", r.id),
                            )
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M16.4249 4.60509L17.4149 3.6151C18.2351 2.79497 19.5648 2.79497 20.3849 3.6151C21.205 4.43524 21.205 5.76493 20.3849 6.58507L19.3949 7.57506M16.4249 4.60509L9.76558 11.2644C9.25807 11.772 8.89804 12.4078 8.72397 13.1041L8 16L10.8959 15.276C11.5922 15.102 12.228 14.7419 12.7356 14.2344L19.3949 7.57506M16.4249 4.60509L19.3949 7.57506"
                              stroke="#C6D92C"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M18.9999 13.5C18.9999 16.7875 18.9999 18.4312 18.092 19.5376C17.9258 19.7401 17.7401 19.9258 17.5375 20.092C16.4312 21 14.7874 21 11.4999 21H11C7.22876 21 5.34316 21 4.17159 19.8284C3.00003 18.6569 3 16.7712 3 13V12.5C3 9.21252 3 7.56879 3.90794 6.46244C4.07417 6.2599 4.2599 6.07417 4.46244 5.90794C5.56879 5 7.21252 5 10.5 5"
                              stroke="#F3F3F6"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          title="Delete"
                          className={`p-1.5 rounded transition-colors ${iconHover}`}
                          onClick={() => openDelete(r)}
                          disabled={isDeleting}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                              fill="url(#paint0_linear_278_6482)"
                            />
                            <path
                              d="M9.5 16.5V10.5"
                              stroke="url(#paint1_linear_278_6482)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              d="M14.5 16.5V10.5"
                              stroke="url(#paint2_linear_278_6482)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <defs>
                              <linearGradient
                                id="paint0_linear_278_6482"
                                x1="12"
                                y1="1.25"
                                x2="12"
                                y2="22.75"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#D92626" />
                                <stop offset="1" stopColor="#AD1F1F" />
                              </linearGradient>
                              <linearGradient
                                id="paint1_linear_278_6482"
                                x1="10"
                                y1="10.5"
                                x2="10"
                                y2="16.5"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#D92626" />
                                <stop offset="1" stopColor="#AD1F1F" />
                              </linearGradient>
                              <linearGradient
                                id="paint2_linear_278_6482"
                                x1="15"
                                y1="10.5"
                                x2="15"
                                y2="16.5"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#D92626" />
                                <stop offset="1" stopColor="#AD1F1F" />
                              </linearGradient>
                            </defs>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
            theme === "dark" ? "bg-black/60" : "bg-black/40"
          }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
              theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
            }`}
          >
            <button
              onClick={closeDelete}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${
                theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>

            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${
                  theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M19.5 5.5L19.0982 12.0062M4.5 5.5L5.10461 15.5248C5.25945 18.0922 5.33688 19.3759 5.97868 20.299C6.296 20.7554 6.7048 21.1407 7.17905 21.4302C7.85035 21.84 8.68108 21.9631 10 22"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M20 15L13 21.9995M20 22L13 15.0005"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Gym</h3>
              <p
                className={`${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-6`}
              >
                Are you sure you want to delete "{deleteTarget.gymName}"?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-[120px] h-[40px] font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
                <button
                  type="button"
                  onClick={closeDelete}
                  className="w-[120px] h-[40px] font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {guestQrModalOpen && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"}`}
        >
          {/* Modal Container */}
          <div
            className={`w-full max-w-[420px] rounded-xl shadow-2xl border font-sans overflow-hidden ${theme === "dark" ? "bg-[#1c1c1e] text-white border-gray-800" : "bg-white text-gray-900 border-gray-200"}`}
          >
            {/* Header */}
            <div
              className={`flex items-center justify-between px-5 py-4 ${theme === "dark" ? "border-b border-gray-800" : "border-b border-gray-200"}`}
            >
              <h2 className="text-lg font-semibold tracking-wide">
                QR Details
              </h2>
              <button
                onClick={() => setGuestQrModalOpen(false)}
                className={`rounded-lg shadow-[inset_0px_-0.94px_0.94px_0px_#96A1AC1F] p-2 transition-colors ${theme === "dark" ? "bg-[#2c2c2e] text-gray-400 hover:bg-[#3a3a3c] hover:text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900"}`}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
                </svg>
              </button>
            </div>

            {/* Content Body */}
            <div className="flex flex-col items-center px-5 pb-6 mt-5">
              {/* QR Code Container */}
              <div
                className="mb-6 rounded-lg bg-white p-3 shadow-sm"
                ref={captureRef}
              >
                {/* Replace src with your actual QR code image source */}
                <QRCodeSVG
                  value={`${import.meta.env.VITE_USER_APP_URL}/gym/${qyGymId}`}
                  size={200}
                  level={"H"}
                />
              </div>

              {/* Direct Link Section */}
              <div className="w-full mb-6">
                <label
                  className={`mb-2 block text-sm font-medium ${theme === "dark" ? "text-[#ADADAD]" : "text-gray-700"}`}
                >
                  Access directly through the link below
                </label>
                <div
                  className={`flex w-full items-center rounded-lg border overflow-hidden ${theme === "dark" ? "border-gray-700 bg-[#121212]" : "border-gray-300 bg-gray-50"}`}
                >
                  <textarea
                    readOnly
                    value={`${import.meta.env.VITE_USER_APP_URL}/gym/${qyGymId}`}
                    rows={2}
                    className={`w-full bg-transparent px-3 py-2.5 text-sm outline-none resize-none break-all ${
                      theme === "dark" ? "text-[#FFFFFF]" : "text-gray-600"
                    }`}
                  />
                  <button
                    onClick={() => {
                      handleCopy();
                    }}
                    className={`flex items-center justify-center border-l px-3 py-4 text-[#8B9D2A] transition-colors ${theme === "dark" ? "border-gray-700 hover:bg-[#2c2c2e]" : "border-gray-300 hover:bg-gray-200"}`}
                    title="Copy to clipboard"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16.9999 9C16.9975 6.04291 16.9528 4.51121 16.092 3.46243C15.9258 3.25989 15.7401 3.07418 15.5376 2.90796C14.4312 2 12.7875 2 9.5 2C6.21252 2 4.56878 2 3.46243 2.90796C3.25989 3.07417 3.07418 3.25989 2.90796 3.46243C2 4.56878 2 6.21252 2 9.5C2 12.7875 2 14.4312 2.90796 15.5376C3.07417 15.7401 3.25989 15.9258 3.46243 16.092C4.51121 16.9528 6.04291 16.9975 9 16.9999"
                        stroke="#F3F3F6"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M9 15C9 12.1716 9 10.7574 9.87868 9.87868C10.7574 9 12.1716 9 15 9H16C18.8284 9 20.2426 9 21.1213 9.87868C22 10.7574 22 12.1716 22 15V16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22H15C12.1716 22 10.7574 22 9.87868 21.1213C9 20.2426 9 18.8284 9 16V15Z"
                        stroke="#C6D92C"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex w-full gap-4">
                <button
                  className="
    flex items-center justify-center gap-3
    w-[170px] h-[42px]
    px-[15px] py-[8px]
    rounded-[10px]
    text-white text-[14px] font-semibold
    bg-[radial-gradient(94.17%_100%_at_52.48%_0%,rgba(198,217,44,0.8)_0%,rgba(83,91,16,0.8)_100%)]
    hover:brightness-110 transition
  "
                  disabled={isDownloading}
                  onClick={handleDownload}
                >
                  {isDownloading ? "Downloading..." : "Download"}
                </button>

                <button
                  onClick={() => setGuestQrModalOpen(false)}
                  className={`flex-1  background: radial-gradient(94.17% 100% at 52.48% 0%, rgba(46, 46, 46, 0.8) 0%, rgba(71, 71, 71, 0.8) 100%);
 rounded-lg border py-2.5 text-sm font-semibold shadow-md transition-all ${theme === "dark" ? "border-gray-600 bg-[#3a3a3c] text-white hover:bg-[#4a4a4c]" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GymList;
